import { injectable } from 'tsyringe';
import { type AiPlatformId, type RecommendationPanelDto } from '@bmai/licenses-api-client';
import { BaseViewModel } from '@/shared/viewmodels/BaseViewModel';
import { isSeatLicensedProviderType, platformIdForProviderType } from '@/shared/ProviderPlatform';
import { reassignmentRecommendations } from './LicenseRecommendations';
import {
    LicensesApiService,
    type LicenseAssignment,
    type LicensePool,
    type PaginatedAssignments,
    type PlatformSpend,
    type SyncOutcome,
} from './LicensesApiService';

export interface MutationLogEntry {
    timestamp: string;
    op: 'assign' | 'unassign';
    userId: string;
    ok: boolean;
    message: string;
}

export interface SyncFailureItem {
    providerLabel: string;
    rawError: string;
}

export interface SyncResultBanner {
    severity: 'success' | 'warning' | 'error';
    headline: string;
    summary: string;
    failures: SyncFailureItem[];
}

const DEFAULT_PAGE_SIZE = 25;
const SPEND_WINDOW_DAYS = 30;
const DAY_MS = 24 * 60 * 60 * 1000;

/** Per-platform seat activity from the optimization endpoint, used to attribute idle seats per connection. */
interface PlatformSeatActivity {
    id: AiPlatformId;
    active: number;
}

@injectable()
export class LicensesPageViewModel extends BaseViewModel {
    pools: LicensePool[] = [];
    selectedConnectionId: string | null = null;
    assignInput: string = '';
    assignRole: string = 'reader';

    loading: boolean = false;
    loadError: string | null = null;

    syncing: boolean = false;
    lastSyncResult: SyncResultBanner | null = null;

    assignments: LicenseAssignment[] = [];
    assignmentsTotal: number = 0;
    assignmentsPage: number = 1;
    assignmentsLoading: boolean = false;
    assignmentsError: string | null = null;

    selectedUserIds: Set<string> = new Set();

    mutating: boolean = false;
    mutationLog: MutationLogEntry[] = [];

    assignModalConnectionId: string | null = null;

    idleByConnectionId: Map<string, number> = new Map();
    totalPool: number = 0;
    totalIdle: number = 0;
    hasSeatActivityData: boolean = false;

    recommendations: RecommendationPanelDto[] = [];
    spendCurrency: string = 'USD';
    estReclaimedMonthly: number = 0;
    hasSpendData: boolean = false;

    private seatActivity: PlatformSeatActivity[] = [];
    private spend: PlatformSpend[] = [];

    constructor(private readonly api: LicensesApiService) {
        super();
    }

    async initialize(): Promise<void> {
        await Promise.all([this.loadPools(), this.loadSeatHealth()]);
    }

    /** Optimization + spend feed the Idle column, summary badges, and reassignment
     *  recommendations. Both are soft dependencies — sync/assign must keep working
     *  when they are unavailable. */
    private async loadSeatHealth(): Promise<void> {
        const [optimization, spend] = await Promise.all([
            this.api.getOptimization(SPEND_WINDOW_DAYS).catch((cause: unknown) => {
                console.debug('[Licenses] optimization unavailable', cause);
                return null;
            }),
            this.loadSpend(),
        ]);
        this.hasSeatActivityData = optimization !== null;
        this.seatActivity = optimization?.platforms ?? [];
        this.spend = spend;
        this.applySeatHealth();
        this.notifyChange();
    }

    private async loadSpend(): Promise<PlatformSpend[]> {
        const end = new Date();
        const start = new Date(end.getTime() - SPEND_WINDOW_DAYS * DAY_MS);
        try {
            return await this.api.getPlatformSpend(start.toISOString(), end.toISOString());
        } catch (cause) {
            console.debug('[Licenses] spend unavailable', cause);
            return [];
        }
    }

    /** Joins pool inventory with platform activity: platform-level active seats are
     *  attributed to each connection proportionally to its assigned seats. */
    private applySeatHealth(): void {
        this.totalPool = this.pools.reduce((sum, pool) => sum + pool.totalSeats, 0);

        const idle = new Map<string, number>();
        if (this.hasSeatActivityData) {
            const platformById = new Map(this.seatActivity.map((p) => [p.id, p]));
            const assignedByPlatform = new Map<AiPlatformId, number>();
            for (const pool of this.pools) {
                const platformId = platformIdForProviderType(pool.providerTypeId);
                if (platformId === null) continue;
                assignedByPlatform.set(platformId, (assignedByPlatform.get(platformId) ?? 0) + pool.assignedSeats);
            }
            for (const pool of this.pools) {
                const platformId = platformIdForProviderType(pool.providerTypeId);
                const platform = platformId !== null ? platformById.get(platformId) : undefined;
                const platformAssigned = platformId !== null ? (assignedByPlatform.get(platformId) ?? 0) : 0;
                const active =
                    platform && platformAssigned > 0
                        ? Math.min(Math.round((platform.active * pool.assignedSeats) / platformAssigned), pool.assignedSeats)
                        : 0;
                idle.set(pool.providerConnectionId, Math.max(pool.assignedSeats - active, 0));
            }
        }
        this.idleByConnectionId = idle;
        this.totalIdle = [...idle.values()].reduce((sum, n) => sum + n, 0);

        this.hasSpendData = this.spend.length > 0;
        const first = this.spend[0];
        if (first && first.currency.length > 0) {
            this.spendCurrency = first.currency;
        }

        const spendByConnection = new Map(this.spend.map((s) => [s.providerConnectionId, s]));
        let reclaimable = 0;
        for (const pool of this.pools) {
            const spendRecord = spendByConnection.get(pool.providerConnectionId);
            const poolIdle = idle.get(pool.providerConnectionId) ?? 0;
            if (spendRecord && pool.assignedSeats > 0) {
                reclaimable += (spendRecord.totalAmount / pool.assignedSeats) * poolIdle;
            }
        }
        this.estReclaimedMonthly = reclaimable;

        this.recommendations = reassignmentRecommendations(
            this.pools.map((pool) => ({
                provider: pool.providerDisplayName,
                idle: this.hasSeatActivityData ? (idle.get(pool.providerConnectionId) ?? 0) : null,
                available: pool.availableSeats,
                total: pool.totalSeats,
            })),
        );
    }

    /** Idle seats for a pool, or null when seat-activity data is unavailable. */
    idleFor(connectionId: string): number | null {
        return this.hasSeatActivityData ? (this.idleByConnectionId.get(connectionId) ?? 0) : null;
    }

    /** Unassigned (free) seats across all pools — the "Available" summary stat. */
    get totalAvailable(): number {
        return this.pools.reduce((sum, pool) => sum + pool.availableSeats, 0);
    }

    private async loadPools(): Promise<void> {
        this.loading = true;
        this.loadError = null;
        this.notifyChange();
        try {
            this.pools = (await this.api.listPools()).filter((pool) => isSeatLicensedProviderType(pool.providerTypeId));
            if (this.selectedConnectionId !== null && !this.pools.some((p) => p.providerConnectionId === this.selectedConnectionId)) {
                this.selectedConnectionId = null;
                this.assignments = [];
                this.assignmentsTotal = 0;
                this.selectedUserIds = new Set();
            }
            this.applySeatHealth();
        } catch (cause) {
            this.loadError = errorMessage(cause);
        } finally {
            this.loading = false;
            this.notifyChange();
        }
    }

    selectedPool(): LicensePool | undefined {
        return this.pools.find((p) => p.providerConnectionId === this.selectedConnectionId);
    }

    setSelectedConnectionId(id: string | null): void {
        this.selectedConnectionId = id;
        this.assignments = [];
        this.assignmentsTotal = 0;
        this.assignmentsPage = 1;
        this.assignmentsError = null;
        this.selectedUserIds = new Set();
        const pool = this.pools.find((p) => p.providerConnectionId === id);
        if (pool?.assignmentRoles && pool.assignmentRoles.length > 0) {
            this.assignRole = pool.assignmentRoles.includes(this.assignRole) ? this.assignRole : pool.assignmentRoles[0]!;
        }
        this.notifyChange();
        if (id) void this.loadAssignments();
    }

    setAssignInput(value: string): void {
        this.assignInput = value;
        this.notifyChange();
    }

    setAssignRole(value: string): void {
        this.assignRole = value;
        this.notifyChange();
    }

    toggleSelected(userId: string): void {
        if (this.selectedUserIds.has(userId)) {
            this.selectedUserIds.delete(userId);
        } else {
            this.selectedUserIds.add(userId);
        }
        this.selectedUserIds = new Set(this.selectedUserIds);
        this.notifyChange();
    }

    setAllSelected(checked: boolean): void {
        this.selectedUserIds = checked ? new Set(this.assignments.map((a) => a.userId)) : new Set();
        this.notifyChange();
    }

    isSelected(userId: string): boolean {
        return this.selectedUserIds.has(userId);
    }

    allVisibleSelected(): boolean {
        return this.assignments.length > 0 && this.assignments.every((a) => this.selectedUserIds.has(a.userId));
    }

    async syncAll(): Promise<void> {
        this.syncing = true;
        this.lastSyncResult = null;
        this.notifyChange();
        try {
            const outcome = await this.api.syncTenant();
            this.lastSyncResult = this.buildSyncBanner(outcome.runs);
        } catch (cause) {
            this.lastSyncResult = thrownSyncBanner(cause);
        } finally {
            this.syncing = false;
            this.notifyChange();
            await this.loadPools();
        }
    }

    async syncSelected(): Promise<void> {
        if (!this.selectedConnectionId) return;
        this.syncing = true;
        this.lastSyncResult = null;
        this.notifyChange();
        try {
            const outcome = await this.api.syncConnection(this.selectedConnectionId);
            this.lastSyncResult = this.buildSyncBanner([outcome]);
        } catch (cause) {
            this.lastSyncResult = thrownSyncBanner(cause);
        } finally {
            this.syncing = false;
            this.notifyChange();
            await this.loadPools();
            await this.loadAssignments();
        }
    }

    async syncPool(connectionId: string): Promise<void> {
        if (this.syncing) return;
        this.syncing = true;
        this.lastSyncResult = null;
        this.notifyChange();
        try {
            const outcome = await this.api.syncConnection(connectionId);
            this.lastSyncResult = this.buildSyncBanner([outcome]);
        } catch (cause) {
            this.lastSyncResult = thrownSyncBanner(cause);
        } finally {
            this.syncing = false;
            this.notifyChange();
            await this.loadPools();
            if (this.selectedConnectionId === connectionId) {
                await this.loadAssignments();
            }
        }
    }

    private buildSyncBanner(runs: SyncOutcome[]): SyncResultBanner {
        if (runs.length === 0) {
            return {
                severity: 'success',
                headline: 'Nothing to sync',
                summary: 'No license-capable connections to sync.',
                failures: [],
            };
        }
        const ok = runs.filter((r) => r.status === 'success').length;
        const partial = runs.filter((r) => r.status === 'partial').length;
        const failed = runs.filter((r) => r.status === 'failed').length;
        const records = runs.reduce((sum, r) => sum + r.recordCount, 0);

        const severity: SyncResultBanner['severity'] =
            ok === runs.length ? 'success' : ok === 0 ? 'error' : 'warning';
        const headline =
            severity === 'error'
                ? 'Sync failed'
                : severity === 'warning'
                    ? 'Sync completed with errors'
                    : 'Sync complete';

        const connectionWord = runs.length === 1 ? 'connection' : 'connections';
        const recordWord = records === 1 ? 'record' : 'records';
        const summary = `${runs.length} ${connectionWord}: ${ok} ok, ${partial} partial, ${failed} failed — ${records} assignment ${recordWord} synced`;

        const failures: SyncFailureItem[] = runs
            .filter((r) => r.status !== 'success' && r.error)
            .map((r) => ({
                providerLabel: this.pools.find((p) => p.providerConnectionId === r.providerConnectionId)?.providerDisplayName ?? r.providerConnectionId,
                rawError: r.error!,
            }));

        return { severity, headline, summary, failures };
    }

    openAssignModal(connectionId: string): void {
        this.assignModalConnectionId = connectionId;
        this.assignInput = '';
        const pool = this.pools.find((p) => p.providerConnectionId === connectionId);
        if (pool?.assignmentRoles && pool.assignmentRoles.length > 0) {
            this.assignRole = pool.assignmentRoles.includes(this.assignRole)
                ? this.assignRole
                : pool.assignmentRoles[0]!;
        }
        this.notifyChange();
    }

    closeAssignModal(): void {
        this.assignModalConnectionId = null;
        this.notifyChange();
    }

    assignModalPool(): LicensePool | undefined {
        return this.pools.find((p) => p.providerConnectionId === this.assignModalConnectionId);
    }

    async assignFromModal(): Promise<void> {
        const targetId = this.assignModalConnectionId;
        if (!targetId) return;
        const ids = parseUserIds(this.assignInput);
        if (ids.length === 0) return;
        const role = this.assignModalPool()?.assignmentRoles && this.assignModalPool()!.assignmentRoles!.length > 0
            ? this.assignRole
            : undefined;
        await this.runBulkForConnection(targetId, 'assign', ids, role, () => {
            this.assignInput = '';
            this.assignModalConnectionId = null;
        });
    }

    async loadAssignments(): Promise<void> {
        if (!this.selectedConnectionId) return;
        this.assignmentsLoading = true;
        this.assignmentsError = null;
        this.notifyChange();
        try {
            const page: PaginatedAssignments = await this.api.getAssignments(
                this.selectedConnectionId,
                this.assignmentsPage,
                DEFAULT_PAGE_SIZE,
            );
            this.assignments = page.items;
            this.assignmentsTotal = page.total;
        } catch (cause) {
            this.assignmentsError = errorMessage(cause);
        } finally {
            this.assignmentsLoading = false;
            this.notifyChange();
        }
    }

    async setAssignmentsPage(page: number): Promise<void> {
        this.assignmentsPage = page;
        await this.loadAssignments();
    }

    async unassign(userId: string): Promise<void> {
        const trimmed = userId.trim();
        if (trimmed.length === 0 || !this.selectedConnectionId) return;
        await this.runBulk('unassign', [trimmed], undefined, () => undefined);
    }

    async assign(): Promise<void> {
        const ids = parseUserIds(this.assignInput);
        if (ids.length === 0 || !this.selectedConnectionId) return;
        await this.runBulk('assign', ids, this.assignRole, () => {
            this.assignInput = '';
        });
    }

    async bulkUnassignSelected(): Promise<void> {
        if (!this.selectedConnectionId || this.selectedUserIds.size === 0) return;
        const ids = [...this.selectedUserIds];
        await this.runBulk('unassign', ids, undefined, () => {
            this.selectedUserIds = new Set();
        });
    }

    private async runBulk(op: 'assign' | 'unassign', userIds: string[], role: string | undefined, onSuccess: () => void): Promise<void> {
        if (!this.selectedConnectionId) return;
        await this.runBulkForConnection(this.selectedConnectionId, op, userIds, role, onSuccess);
    }

    private async runBulkForConnection(
        connectionId: string,
        op: 'assign' | 'unassign',
        userIds: string[],
        role: string | undefined,
        onSuccess: () => void,
    ): Promise<void> {
        this.mutating = true;
        this.notifyChange();
        let anySucceeded = false;
        try {
            const result = op === 'assign'
                ? await this.api.bulkAssign(connectionId, userIds, role)
                : await this.api.bulkUnassign(connectionId, userIds);
            const newEntries = result.items.map((item): MutationLogEntry => ({
                timestamp: new Date().toISOString(),
                op,
                userId: item.userId,
                ok: item.ok,
                message: item.ok ? `${op} succeeded` : (item.error ?? `${op} failed`),
            }));
            this.mutationLog = [...newEntries, ...this.mutationLog];
            anySucceeded = result.succeeded > 0;
            if (anySucceeded) onSuccess();
        } catch (cause) {
            this.mutationLog = [
                { timestamp: new Date().toISOString(), op, userId: `(bulk ${userIds.length})`, ok: false, message: errorMessage(cause) },
                ...this.mutationLog,
            ];
        } finally {
            this.mutating = false;
            this.notifyChange();
        }

        if (anySucceeded) {
            await this.syncPool(connectionId);
        }
    }

}

function thrownSyncBanner(cause: unknown): SyncResultBanner {
    return {
        severity: 'error',
        headline: 'Sync failed',
        summary: 'The sync request could not be completed.',
        failures: [{ providerLabel: 'Request', rawError: errorMessage(cause) }],
    };
}

function errorMessage(cause: unknown): string {
    if (cause instanceof Error) return cause.message;
    return String(cause);
}

function parseUserIds(raw: string): string[] {
    const seen = new Set<string>();
    const out: string[] = [];
    for (const piece of raw.split(/[\s,;]+/)) {
        const trimmed = piece.trim();
        if (trimmed.length === 0) continue;
        if (seen.has(trimmed)) continue;
        seen.add(trimmed);
        out.push(trimmed);
    }
    return out;
}
